import { startOfWeek, toDateKey } from './index.js';
/**
 * Classroom jobs.
 *
 * The board by the door: Line Leader, Trash Helper, iPad Watcher. Three things
 * shape everything here.
 *
 *  1. **A job's turn is a period, not a date.** Line Leader is a month;
 *     Trash Helper is a week. So an assignment belongs to a *period key* —
 *     '2026-08' or the Sunday that starts a week — and the history is just
 *     every period that has been recorded. Nothing needs to be re-dated when
 *     the calendar rolls over; the new period simply has nothing in it yet.
 *
 *  2. **Fairness is countable.** Every teacher who has run a job board has
 *     had the same argument with a six-year-old about whose turn it is, so
 *     how many turns each child has had is a number the page shows rather
 *     than something she has to remember.
 *
 *  3. **It empties itself in June.** Jobs are hers and carry over; the
 *     children in them do not. Turns are tagged with the school year when the
 *     year is archived, exactly as the roster and the assessment data are.
 */
export const JOB_CADENCES = ['weekly', 'monthly'];
export const JOB_CADENCE_LABELS = {
    weekly: 'Weekly',
    monthly: 'Monthly',
};
/** 'These change every Monday' — the line under the tab. */
export const JOB_CADENCE_BLURBS = {
    weekly: 'These change every week.',
    monthly: 'These change at the start of each month.',
};
/**
 * How far back a repeat counts.
 *
 * Three turns: a child who had Line Leader in March is fair game in July but
 * not in April. Long enough to matter, short enough that a class of
 * twenty-five is not permanently ineligible for everything.
 */
export const REPEAT_WINDOW = 3;
/* ------------------------------------------------------------- periods */
/**
 * The period key a date falls in.
 *
 * Weekly keys are the Monday that starts the week — the same convention as
 * `weekKeyOf`, which the lesson plans and to-dos already use. One week
 * convention in the app, not two; a job board that starts its week on a
 * different day from the lesson plan is a job board nobody can read.
 * Monthly keys are 'YYYY-MM'. Both sort lexicographically into date order,
 * which is what makes "the last three turns" a slice rather than a query.
 */
export function periodOf(cadence, date = new Date()) {
    if (cadence === 'weekly')
        return toDateKey(startOfWeek(date));
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
/** The period `delta` turns away from this one. */
export function shiftPeriod(cadence, period, delta) {
    if (cadence === 'weekly') {
        const [y, m, d] = period.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        date.setDate(date.getDate() + delta * 7);
        return toDateKey(date);
    }
    const [y, m] = period.split('-').map(Number);
    /*
     * Day 1, so the month arithmetic cannot fall through. `new Date(2026, 0, 31)`
     * shifted a month is the 3rd of March, because February has no 31st — and a
     * job board that skips February is a bug nobody would find until February.
     */
    const date = new Date(y, m - 1, 1);
    date.setMonth(date.getMonth() + delta);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
/** The last `count` periods ending just before `period`, newest first. */
export function previousPeriods(cadence, period, count) {
    return Array.from({ length: count }, (_, i) => shiftPeriod(cadence, period, -(i + 1)));
}
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
/** 'August 2026', or 'Aug 3 – 7' — what to put above the board. */
export function periodLabel(cadence, period) {
    if (cadence === 'monthly') {
        const [y, m] = period.split('-').map(Number);
        return `${MONTHS[(m ?? 1) - 1]} ${y}`;
    }
    const [y, m, d] = period.split('-').map(Number);
    const from = new Date(y, m - 1, d);
    const to = new Date(y, m - 1, d + 4);
    const month = from.toLocaleDateString('en-US', { month: 'short' });
    const toMonth = to.toLocaleDateString('en-US', { month: 'short' });
    return month === toMonth
        ? `${month} ${from.getDate()} – ${to.getDate()}`
        : `${month} ${from.getDate()} – ${toMonth} ${to.getDate()}`;
}
/** Whether a period is the one happening now. */
export function isCurrentPeriod(cadence, period, now = new Date()) {
    return period === periodOf(cadence, now);
}
export function periodStanding(cadence, period, now = new Date()) {
    const here = periodOf(cadence, now);
    if (period === here)
        return 'current';
    /*
     * String comparison, which works because both key shapes are written
     * biggest-unit-first and zero-padded — '2026-09-07' sorts after
     * '2026-08-31', and '2026-09' after '2026-08'. That is the same property
     * that makes "the last three turns" a slice rather than a query.
     */
    return period < here ? 'past' : 'future';
}
export function pickForJob(candidates, howMany) {
    const rank = (a, b) => a.timesOnThisJob - b.timesOnThisJob ||
        a.timesOnAnyJob - b.timesOnAnyJob ||
        a.order - b.order ||
        a.studentId.localeCompare(b.studentId);
    const clear = candidates.filter((c) => !c.flagged).sort(rank);
    const flagged = candidates.filter((c) => c.flagged).sort(rank);
    return [...clear, ...flagged].slice(0, Math.max(0, howMany)).map((c) => c.studentId);
}
/* ------------------------------------------------------------ shipped */
/**
 * The jobs a first-grade room already has.
 *
 * Seeded into her own documents on first use, like the sub plans, so every one
 * of them can be renamed, re-timed or deleted. The cadences are the ones these
 * jobs usually run at: the line leader is a month, the small daily chores turn
 * over weekly so more children get a go.
 */
export const SHIPPED_JOBS = [
    {
        name: 'Line leader',
        cadence: 'monthly',
        description: 'Leads the line to specials, lunch and recess.',
        wants: 1,
        sortOrder: 0,
    },
    {
        name: 'Caboose',
        cadence: 'monthly',
        description: 'Last in line, and holds the door.',
        wants: 1,
        sortOrder: 1,
    },
    {
        name: 'Group leader',
        cadence: 'monthly',
        description: 'Hands out and collects the work at each table.',
        wants: 4,
        sortOrder: 2,
    },
    {
        name: 'Trash helper',
        cadence: 'weekly',
        description: 'Checks the floor before we leave and takes the bin out.',
        wants: 2,
        sortOrder: 3,
    },
    {
        name: 'iPad watcher',
        cadence: 'weekly',
        description: 'Counts the iPads back in and puts them on to charge.',
        wants: 1,
        sortOrder: 4,
    },
    {
        name: 'Mailbox watcher',
        cadence: 'weekly',
        description: 'Puts the papers in the mailboxes at the end of the day.',
        wants: 1,
        sortOrder: 5,
    },
    {
        name: 'Librarian',
        cadence: 'weekly',
        description: 'Tidies the book corner and puts the books back the right way.',
        wants: 1,
        sortOrder: 6,
    },
    {
        name: 'Weather watcher',
        cadence: 'weekly',
        description: 'Checks the weather and changes the chart at morning meeting.',
        wants: 1,
        sortOrder: 7,
    },
];
//# sourceMappingURL=jobs.js.map